import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Clock, Loader2, Eye, CheckCircle2, Film, Download, ShieldCheck,
  AlertTriangle, PackageCheck, PlayCircle, XCircle, HardDrive,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtClock = (sec) => {
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};
const fmtMB = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const STATUS_UI = {
  pending: { label: "Pendiente", cls: "bg-[#F0EBE4] text-[#8A7F73] border-[#DDD2C2]" },
  analyzing: { label: "Analizando…", cls: "bg-[#FFF3E9] text-[#C2570F] border-[#F26F21]/40" },
  ready: { label: "Listo para revisar", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]" },
  validated: { label: "Validado", cls: "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]" },
  rendered: { label: "Renderizado", cls: "bg-[#E8F0FE] text-[#1A56DB] border-[#C3D4F5]" },
};

export const ChunkPanel = ({ videoId, style, strength, onReviewChunk, activeIndex, onResume }) => {
  const [detail, setDetail] = useState(null);
  const [job, setJob] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [freeOriginal, setFreeOriginal] = useState(false);
  const exportingRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [{ data: d }, { data: s }] = await Promise.all([
        axios.get(`${API}/videos/${videoId}`),
        axios.get(`${API}/videos/${videoId}/status`),
      ]);
      setDetail(d);
      setJob(s);
      if (exportingRef.current && ["done", "error", "cancelled"].includes(s.status)) {
        exportingRef.current = false;
        setExporting(false);
        if (s.status === "done") {
          const f = s.forensic || {};
          toast.success(`Exportación completada · ${f.status || ""}`);
        } else if (s.status === "error") {
          toast.error(`La exportación falló: ${s.step}`);
        } else {
          toast("Exportación cancelada.");
        }
      }
    } catch {}
  }, [videoId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!detail?.task || !detail?.chunks?.length) return null;

  const { task, chunks, exports = [] } = detail;
  const fps = task.fps || 25;
  const n = task.n_chunks;
  const jobActive = ["processing", "analyzing", "paused"].includes(job?.status);
  const pending = chunks.filter((c) => ["pending", "analyzing"].includes(c.status));
  const selArr = [...sel].sort((a, b) => a - b);
  const contiguous = selArr.length > 0 && selArr.every((v, i) => i === 0 || v === selArr[i - 1] + 1);
  const selReady = selArr.every((i) => !["pending", "analyzing"].includes(chunks.find((c) => c.index === i)?.status));

  const toggle = (i) =>
    setSel((prev) => {
      const nx = new Set(prev);
      nx.has(i) ? nx.delete(i) : nx.add(i);
      return nx;
    });

  const selectAllReady = () => {
    const ready = chunks.filter((c) => !["pending", "analyzing"].includes(c.status)).map((c) => c.index);
    setSel((prev) => (prev.size === ready.length ? new Set() : new Set(ready)));
  };

  const validate = async (i) => {
    try {
      const { data } = await axios.post(`${API}/videos/${videoId}/chunks/${i}/validate`);
      toast.success(data.validated ? `Tramo ${i}/${n} validado ✓` : `Validación del tramo ${i}/${n} retirada`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo validar el tramo.");
    }
  };

  const startExport = async () => {
    try {
      await axios.post(`${API}/videos/${videoId}/export`, { chunks: selArr, style: `${style}|${strength || 3}`, free_original: freeOriginal });
      exportingRef.current = true;
      setExporting(true);
      toast(`Exportando tramos ${selArr[0]}/${n} → ${selArr[selArr.length - 1]}/${n}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la exportación.");
    }
  };

  const cancelJob = async () => {
    try { await axios.post(`${API}/videos/${videoId}/job/cancel`); toast("Cancelando…"); } catch {}
  };

  return (
    <section data-testid="chunk-panel" className="mt-6 rounded-3xl bg-[#FFF7F0] border border-[#F5E7D8] p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F]">Checkpoints por tramo</p>
          <p className="text-[13px] text-[#8A7F73] mt-0.5">
            Tarea <span data-testid="task-master-id" className="font-extrabold text-[#241E18] tabular-nums">{task.master_id}</span>
            {" · "}{n} tramos de {task.chunk_minutes} min · revisa y exporta sin esperar al resto
          </p>
        </div>
        <button onClick={selectAllReady} className="text-[11.5px] font-bold text-[#C2570F] hover:underline" data-testid="chunk-select-ready-btn">
          Seleccionar todos los listos
        </button>
      </div>

      {pending.length > 0 && !jobActive && (
        <div data-testid="task-continue-banner" className="flex flex-wrap items-center gap-3 mb-4 rounded-2xl bg-[#241E18] px-4 py-3">
          <Clock size={16} className="text-[#FFB81C] shrink-0" />
          <p className="flex-1 text-[13px] font-bold text-white">
            Quedan {pending.length} tramos sin analizar. ¿Continuar con {pending[0].index}/{n} → {n}/{n}? Lo ya hecho no se repite.
          </p>
          <button
            data-testid="task-continue-btn"
            onClick={onResume}
            className="h-9 px-5 rounded-full bg-[#F26F21] text-white text-[12.5px] font-extrabold hover:bg-[#D95F14] transition-colors inline-flex items-center gap-1.5"
          >
            <PlayCircle size={14} /> Continuar tarea
          </button>
        </div>
      )}

      <div className="max-h-[340px] overflow-y-auto pr-1 space-y-1.5" data-testid="chunk-list">
        {chunks.map((c) => {
          const ui = STATUS_UI[c.status] || STATUS_UI.pending;
          const reviewable = !["pending", "analyzing"].includes(c.status);
          const isVal = !!c.validated || c.status === "validated";
          const active = activeIndex === c.index;
          const chunkExport = exports.find((e) => (e.chunks || []).length === 1 && e.chunks[0] === c.index);
          return (
            <div
              key={c.index}
              data-testid={`chunk-row-${c.index}`}
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2 transition-all ${
                active ? "bg-white border-2 border-[#F26F21]" : "bg-white/70 border border-[#F0E2D2]"
              }`}
            >
              <input
                type="checkbox"
                data-testid={`chunk-check-${c.index}`}
                checked={sel.has(c.index)}
                disabled={!reviewable}
                onChange={() => toggle(c.index)}
                className="accent-[#F26F21] w-4 h-4 shrink-0 cursor-pointer disabled:opacity-30"
              />
              <span className="w-14 text-[12.5px] font-extrabold text-[#241E18] tabular-nums shrink-0">{c.index}/{n}</span>
              <span className="text-[11px] font-semibold text-[#8A7F73] tabular-nums shrink-0 hidden sm:inline">
                {fmtClock((c.start - 1) / fps)}–{fmtClock(c.end / fps)}
              </span>
              <span className={`px-2 py-0.5 rounded-md border text-[10px] font-extrabold ${ui.cls} inline-flex items-center gap-1`}>
                {c.status === "analyzing" && <Loader2 size={10} className="animate-spin" />}
                {c.status === "validated" && <CheckCircle2 size={10} />}
                {c.status === "rendered" && <Film size={10} />}
                {ui.label}
              </span>
              <span className="flex-1 text-[11px] font-semibold text-[#8A7F73] tabular-nums truncate">
                {reviewable ? `${c.count} elementos` : ""}
              </span>
              {chunkExport && (
                <a
                  data-testid={`chunk-open-btn-${c.index}`}
                  href={`${API}/videos/${videoId}/exports/${chunkExport.id}/file`}
                  target="_blank"
                  rel="noreferrer"
                  title="Abrir el vídeo procesado de este tramo en otra pestaña"
                  className="h-7 px-3 rounded-lg bg-[#241E18] text-white text-[11px] font-bold hover:bg-black transition-all inline-flex items-center gap-1 shrink-0"
                >
                  <PlayCircle size={11} className="text-[#FFB81C]" /> Abrir
                </a>
              )}
              {reviewable && (
                <>
                  <button
                    data-testid={`chunk-review-btn-${c.index}`}
                    onClick={() => onReviewChunk(c, task)}
                    className="h-7 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[11px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center gap-1 shrink-0"
                  >
                    <Eye size={11} className="text-[#F26F21]" /> Revisar
                  </button>
                  <button
                    data-testid={`chunk-validate-btn-${c.index}`}
                    onClick={() => validate(c.index)}
                    className={`h-7 px-3 rounded-lg text-[11px] font-bold transition-all inline-flex items-center gap-1 shrink-0 ${
                      isVal
                        ? "bg-[#EAF7EE] border border-[#BFE5CB] text-[#1E6B3A]"
                        : "bg-white border border-[#F0E2D2] text-[#241E18] hover:border-[#2FA45A]"
                    }`}
                  >
                    <CheckCircle2 size={11} className={isVal ? "text-[#2FA45A]" : "text-[#8A7F73]"} />
                    {isVal ? "Validado" : "Validar"}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 rounded-2xl bg-white border border-[#F0E2D2] px-4 py-3">
        <PackageCheck size={16} className="text-[#F26F21] shrink-0" />
        <p className="flex-1 text-[12.5px] font-semibold text-[#57503F]">
          {selArr.length === 0
            ? "Marca los tramos a exportar (rango contiguo, ej. 1 → 12)."
            : !contiguous
            ? "La selección debe ser contigua (sin huecos)."
            : !selReady
            ? "Hay tramos seleccionados aún sin analizar."
            : `Exportar ${selArr[0]}/${n} → ${selArr[selArr.length - 1]}/${n} (${selArr.length} tramos) con informe forense del rango.`}
        </p>
        {exporting || (jobActive && job?.step?.includes("Exportando")) ? (
          <span className="inline-flex items-center gap-2">
            <span className="text-[12px] font-bold text-[#C2570F] tabular-nums inline-flex items-center gap-1.5">
              <Loader2 size={13} className="animate-spin" /> {job?.progress || 0}%{job?.frames_total ? ` · fotograma ${(job.frames_done || 0).toLocaleString("es-ES")}/${job.frames_total.toLocaleString("es-ES")}` : ""} · {job?.step || "Exportando…"}
            </span>
            <button data-testid="export-cancel-btn" onClick={cancelJob} className="h-8 px-3 rounded-full border border-[#E4574A]/40 text-[#D93025] text-[11.5px] font-bold hover:bg-[#E4574A]/10 transition-colors inline-flex items-center gap-1">
              <XCircle size={12} /> Cancelar
            </button>
          </span>
        ) : (
          <button
            data-testid="export-selection-btn"
            onClick={startExport}
            disabled={!contiguous || !selReady || jobActive}
            className="h-10 px-6 rounded-full bg-[#F26F21] text-white text-[13px] font-extrabold hover:bg-[#D95F14] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
          >
            <Download size={14} /> Exportar selección
          </button>
        )}
      </div>
      <label className="flex items-center gap-2 mt-2 px-1 cursor-pointer w-fit">
        <input
          type="checkbox"
          data-testid="export-free-original"
          checked={freeOriginal}
          onChange={(e) => setFreeOriginal(e.target.checked)}
          className="accent-[#F26F21] w-3.5 h-3.5"
        />
        <span className="text-[11.5px] font-semibold text-[#8A7F73]">
          Liberar espacio al terminar: eliminar el original del disco (queda la copia de seguridad y se restaura si lo necesitas)
        </span>
      </label>

      {exports.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1.5">Exportaciones de esta tarea</p>
          <div className="space-y-1.5">
            {exports.slice().reverse().map((e) => (
              <div key={e.id} data-testid={`export-item-${e.id}`} className="flex flex-wrap items-center gap-2.5 rounded-xl bg-white border border-[#F0E2D2] px-3 py-2">
                <Film size={14} className="text-[#F26F21] shrink-0" />
                <span className="text-[12.5px] font-extrabold text-[#241E18] tabular-nums">Tramos {e.label}</span>
                <span className="text-[11px] font-semibold text-[#8A7F73]">{fmtMB(e.size || 0)}</span>
                {e.forensic && (
                  <span className={`px-2 py-0.5 rounded-md border text-[10px] font-extrabold inline-flex items-center gap-1 ${
                    e.forensic.ok ? "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]" : "bg-[#FDECEA] text-[#B3261E] border-[#F5C6C0]"
                  }`}>
                    {e.forensic.ok ? <ShieldCheck size={10} /> : <AlertTriangle size={10} />}
                    {e.forensic.status}
                  </span>
                )}
                <span className="flex-1" />
                {e.drive?.link && (
                  <a
                    data-testid={`export-drive-link-${e.id}`}
                    href={e.drive.link}
                    target="_blank"
                    rel="noreferrer"
                    className="h-7 px-3 rounded-lg border border-[#BFE5CB] bg-[#EAF7EE] text-[#1E6B3A] text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-[#DDF2E4] transition-colors"
                  >
                    <HardDrive size={11} /> En Drive
                  </a>
                )}
                <a
                  data-testid={`export-open-${e.id}`}
                  href={`${API}/videos/${videoId}/exports/${e.id}/file`}
                  target="_blank"
                  rel="noreferrer"
                  className="h-7 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[#241E18] text-[11px] font-bold inline-flex items-center gap-1.5 hover:border-[#F26F21] transition-colors"
                >
                  <PlayCircle size={11} className="text-[#F26F21]" /> Abrir
                </a>
                <a
                  data-testid={`export-download-${e.id}`}
                  href={`${API}/videos/${videoId}/exports/${e.id}/file?download=1`}
                  className="h-7 px-3 rounded-lg bg-[#241E18] text-white text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-black transition-colors"
                >
                  <Download size={11} /> Descargar
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ChunkPanel;
